import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Download, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

// ── Asset manifest ─────────────────────────────────────────────────────────────
const SOCIAL_CARDS = [
  {
    id: 'hero',
    title: 'Hero Announcement',
    description: '"You\'re Overpaying the IRS." — great for launch posts & Twitter/X.',
    size: '1:1 · 1024×1024',
    use: 'Twitter/X · LinkedIn · Instagram',
    url: 'https://static.prod-images.emergentagent.com/jobs/c1ba3782-2eba-4347-bee5-0a0bd922667f/images/d04407bb39451be3da3fc13504ed567003624e80fec63fe35cf3a64d00dc5580.png',
  },
  {
    id: 'stat',
    title: 'Tax Savings Stat Card',
    description: '"$36,847 in missed savings identified" — proof of value post.',
    size: '1:1 · 1024×1024',
    use: 'Twitter/X · LinkedIn · Instagram',
    url: 'https://static.prod-images.emergentagent.com/jobs/c1ba3782-2eba-4347-bee5-0a0bd922667f/images/50f5f80496472298a2e68d1431901e85c9061f22a252397af02b156387b37c8c.png',
  },
  {
    id: 'founding',
    title: 'Founding Member Offer',
    description: '"$149/yr — 100 founding spots" — scarcity & urgency for outreach.',
    size: '1:1 · 1024×1024',
    use: 'Twitter/X · LinkedIn · DMs',
    url: 'https://static.prod-images.emergentagent.com/jobs/c1ba3782-2eba-4347-bee5-0a0bd922667f/images/81cfff36ea57b9df41bb76bed7f960c3862aa64d78de6fe5839c4b532b63b4a5.png',
  },
  {
    id: 'coverage',
    title: 'IRS Knowledge Coverage',
    description: '"94 IRS chunks · 50 states · 100% cited" — builds trust in AI accuracy.',
    size: '1:1 · 1024×1024',
    use: 'Twitter/X · LinkedIn · Product Hunt',
    url: 'https://static.prod-images.emergentagent.com/jobs/c1ba3782-2eba-4347-bee5-0a0bd922667f/images/1f4d9788b42a2b85971ae6847eedee48ba52fb5a7fb46a26678613eed515af45.png',
  },
  {
    id: 'grid',
    title: 'Feature Grid (Building in Public)',
    description: '"Built for Freelancers. Contractors. Solopreneurs." — product overview.',
    size: '3:2 · 1536×1024',
    use: 'Twitter/X · LinkedIn · Newsletter',
    url: 'https://static.prod-images.emergentagent.com/jobs/c1ba3782-2eba-4347-bee5-0a0bd922667f/images/49e8f8fc192687e3b407b9826136680a53809018cd9ea183fb5d3836c5dae7b8.png',
  },
];

const APP_SCREENSHOTS = [
  {
    id: 'landing',
    title: 'Landing Page',
    description: 'Conversion-optimized landing with founding member scarcity ticker.',
    url: 'https://import-and-build-3.preview.emergentagent.com',
    caption: '"No credit card required · 30-day free trial"',
  },
  {
    id: 'dashboard',
    title: 'Tax Leak Audit Dashboard',
    description: '$36,092 in savings identified — benchmark card showing "Above Average".',
    url: 'https://import-and-build-3.preview.emergentagent.com/dashboard',
    caption: 'The core product value — instantly visible on login.',
  },
  {
    id: 'chat',
    title: 'AI Advisors (RAG-Powered)',
    description: 'Carlotta (AI Tax Advisor) citing IRS Publication 587 in real time.',
    url: 'https://import-and-build-3.preview.emergentagent.com/chat',
    caption: 'IRS-grounded answers, not generic AI responses.',
  },
  {
    id: 'pricing',
    title: 'Pricing Page',
    description: 'Monthly $19 · Yearly $149 founding rate with live slot counter.',
    url: 'https://import-and-build-3.preview.emergentagent.com/pricing',
    caption: '"0 / 100 founding spots claimed — 100 left"',
  },
  {
    id: 'strategy',
    title: 'Tax Recovery Playbook',
    description: 'Personalized tax strategies with AI insights and deadlines.',
    url: 'https://import-and-build-3.preview.emergentagent.com/strategy',
    caption: 'Positioning pivot: from dashboard → action OS.',
  },
];

const COPY_SNIPPETS = [
  {
    label: 'Twitter/X — IRS Knowledge Coverage Thread',
    text: `People ask: "How much does the AI actually know?"

Here's what SolelyTax AI's advisors are grounded in:

→ 94 IRS publication chunks indexed
→ All 50 US states + DC income tax rules
→ Every answer cites the specific IRS pub and section
→ Topics: Business expenses, S-Corp, retirement plans, home office, mileage, crypto, QBI, depreciation, 1099-K, worker classification, state taxes

Not a chatbot guessing at tax rules.
An AI advisor grounded in actual IRS source material.

Try it free for 30 days — no credit card.
solelytax.ai`,
  },
  {
    label: 'Twitter/X Launch Thread (Hook)',
    text: `We built an AI that helps you see which tax strategies you're not using.

Most freelancers & contractors have unexplored tax strategy opportunities.

SolelyTax AI analyzes your finances, identifies potential strategies (S-Corp, home office, mileage, retirement), and shows you estimated value ranges worth discussing with your CPA.

30-day free trial. No credit card. 100 founding member spots at $149/yr.

solelytax.ai`,
  },
  {
    label: 'LinkedIn Post (Founder Story)',
    text: `I built SolelyTax AI for the freelancer I was 3 years ago — paying a CPA $2,000/yr but never having the right questions to ask.

I ran it on my own finances first: the tool identified $36,847 in estimated tax strategy opportunity I hadn't discussed with my CPA yet.

Not from exotic strategies — from basics they didn't know about:
→ S-Corp election: $16,853/yr
→ SEP-IRA contribution: $17,250/yr  
→ Health insurance deduction: $1,800/yr

We're opening 100 founding member spots at $149/yr (normally $228/yr monthly).

If you're a freelancer, contractor, or solopreneur earning $80K+, this is worth your 30-day free trial.

solelytax.ai — No credit card required.`,
  },
  {
    label: 'LinkedIn Post (Trust / IRS Coverage)',
    text: `The #1 concern people have about AI tax advice: "How do I know it's accurate?"

Fair question. Here's our answer for SolelyTax AI:

Every response from our AI advisors is grounded in 94 indexed IRS publication chunks:
• All 50 US states' income tax rules
• IRS Pub 535 (business expenses), Pub 463 (vehicle/travel), Pub 587 (home office)
• S-Corp rules, reasonable compensation standards, Form 2553 deadlines
• SEP-IRA/Solo 401k contribution limits and deadlines
• Cryptocurrency guidance (Notice 2014-21, Rev. Rul. 2023-14)
• Full Schedule C line-by-line guidance
• QBI/Section 199A phase-out calculations
• New 1099-K threshold rules ($5,000 for 2024)

When Carlotta (our AI Tax Advisor) answers a question, she cites the specific IRS publication and section.

Not opinions. IRS-sourced guidance.

solelytax.ai — 30-day free trial, no credit card.`,
  },
  {
    label: 'Cold DM / Direct Outreach',
    text: `Hey [Name] — saw you're a [freelancer/consultant/contractor].

Quick question: do you know which tax strategies might apply to your freelance income?

I built SolelyTax AI — it runs a Tax Leak Audit on your finances and shows you estimated value ranges for strategies worth exploring with your CPA (S-Corp, home office, mileage, retirement).

30-day free trial, no credit card. One of 100 founding member spots available at $149/yr.

Worth a look: solelytax.ai`,
  },
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button size="sm" variant="outline" onClick={handle} className="flex-shrink-0">
      {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
};

export default function FounderKit() {
  return (
    <div className="min-h-screen bg-[#0A0E17] text-white">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 pt-16 pb-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 relative flex items-center justify-center flex-shrink-0">
            <div className="absolute inset-0 bg-emerald-500 rounded-lg transform rotate-45" />
            <div className="relative w-3 h-3 bg-white z-10" />
          </div>
          <span className="text-lg font-semibold"><span className="text-white">Solely</span><span className="text-emerald-400">Tax</span></span>
        </div>
        <Badge className="mb-4 bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">Founder Creative Kit</Badge>
        <h1 className="text-4xl font-bold tracking-tight mb-3">Build In Public Assets</h1>
        <p className="text-slate-400 text-lg max-w-2xl">
          Social cards, app screenshots, and ready-to-post copy for founder outreach,
          Twitter/X threads, LinkedIn posts, and cold DMs.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20 space-y-16">

        {/* Social Cards */}
        <section>
          <h2 className="text-xl font-bold mb-1">Social Cards</h2>
          <p className="text-slate-500 text-sm mb-6">Right-click → Save image, or click Download.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {SOCIAL_CARDS.map(card => (
              <Card key={card.id} className="bg-slate-900/60 border-slate-700/50 overflow-hidden">
                <div className="aspect-square sm:aspect-auto sm:h-52 overflow-hidden bg-slate-800">
                  <img
                    src={card.url}
                    alt={card.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-white text-sm">{card.title}</h3>
                    <Badge className="text-xs bg-slate-800 text-slate-400 border-slate-700 shrink-0">{card.size}</Badge>
                  </div>
                  <p className="text-slate-400 text-xs mb-1">{card.description}</p>
                  <p className="text-emerald-500 text-xs mb-3">{card.use}</p>
                  <a href={card.url} download target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Download
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* App Screenshots */}
        <section>
          <h2 className="text-xl font-bold mb-1">App Screenshots</h2>
          <p className="text-slate-500 text-sm mb-6">Live screenshots from the app — click to open in browser, then screenshot yourself for highest quality.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APP_SCREENSHOTS.map(ss => (
              <a key={ss.id} href={ss.url} target="_blank" rel="noopener noreferrer"
                className="group block bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/40 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-white text-sm group-hover:text-emerald-400 transition-colors">{ss.title}</h3>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400 flex-shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-slate-400 text-xs mb-2">{ss.description}</p>
                <p className="text-emerald-500/80 text-xs italic">{ss.caption}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Copy Snippets */}
        <section>
          <h2 className="text-xl font-bold mb-1">Ready-to-Post Copy</h2>
          <p className="text-slate-500 text-sm mb-6">Personalize with your name and story. These are starting points, not scripts.</p>
          <div className="space-y-4">
            {COPY_SNIPPETS.map((snippet, i) => (
              <Card key={i} className="bg-slate-900/60 border-slate-700/50">
                <CardHeader className="pb-2 flex-row items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-white">{snippet.label}</CardTitle>
                  <CopyButton text={snippet.text} />
                </CardHeader>
                <CardContent>
                  <pre className="text-slate-400 text-xs leading-relaxed whitespace-pre-wrap font-sans">{snippet.text}</pre>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Key stats for posts */}
        <section>
          <h2 className="text-xl font-bold mb-4">Key Stats to Use in Posts</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { stat: '$36,847', label: 'Avg savings identified per user (2025 data)' },
              { stat: '94', label: 'IRS publication chunks in the RAG knowledge base' },
              { stat: '50', label: 'US states covered with income tax rules' },
              { stat: '8', label: 'IRS publication topics indexed (Pub 463, 535, 587, 560, 946, 505, 15-B, QBI)' },
              { stat: '100', label: 'Founding member spots ($149/yr locked in forever)' },
              { stat: '30 days', label: 'Free trial, no credit card required' },
            ].map(({ stat, label }) => (
              <div key={stat} className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4">
                <p className="text-2xl font-bold text-emerald-400 mb-1">{stat}</p>
                <p className="text-slate-500 text-xs">{label}</p>
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
